import React, {Component} from "react";
import _ from "lodash";
import {withRouter} from "react-router";
import { LEGAL_ADVICE_TEXT } from "../../helpers/variables";
import { PERCENTAGE_PREMIUMS_AVAILABLE } from "../../helpers/variables";
import { PREMIUM_SETTINGS_COPY_FROM_PARAM, PRIMARY, PS_TYPE_DEFAULT, PS_TYPE_PERCENTAGE, PS_TYPE_PERCENTAGE_CASE_TYPES, RIGHT, TERTIARY } from "../../helpers/constants";
import {Button, FormGroup } from "arag-ui-lib";
import CONTENT_STRINGS from "../../helpers/contentStrings";
import { FORM_GROUP_VARIANTS } from "../../helpers/formGroupVariants";
import { formatSolicitorOptionLabel } from "../../helpers/functions";

const getCaseTypesOptions = (caseTypes, premiumSettingType) => {
    if(premiumSettingType === PS_TYPE_PERCENTAGE && PERCENTAGE_PREMIUMS_AVAILABLE){
        return [{key: '', label: ''}, ...caseTypes.filter(ct => PS_TYPE_PERCENTAGE_CASE_TYPES.includes(ct.machineName)).map(caseType => {
            return {
                key: caseType.id,
                label: caseType.name
            }
        })]
    } else {
        return [{key: '', label: ''}, ...caseTypes.map(caseType => ({
            key: caseType.id,
            label: caseType.name
        }))]
    }
};

const getSubCaseTypesOptions = (subCases, selectedCaseTypeId) => {
    if(!selectedCaseTypeId) return [{key: '', label: ''}];
    return [{key: '', label: ''}, ..._.filter(subCases, item => {
        return  item.caseTypeId.toString() === selectedCaseTypeId
    }).map(item => ({key: item.id, label: item.name}))];
};

const getGroupSubCaseTypesOptions = (caseTypes, selectedCaseTypeId) => {
    const o = [];
    _.forEach(caseTypes, ctype => {
        if(ctype.id.toString() === selectedCaseTypeId){
            _.forEach(ctype.subCaseTypes, sctype => {
                o.push(sctype)
            })
        }
    });
    return [{key: '', label: ''}, ...o.map(item => {return {key: item.id, label: item.name}})];
};


class PremiumSettingsCopyForm extends Component {
    constructor(props){
        super(props);
        this.state = {
            solicitorGroup: "",
            workSource: "",
            caseType: "",
            subCaseType: "",
        }
    }

    onGroupChange = (option) => {
        // Bug fix: guard against a cleared selection (option === null), matching the
        // guard referralSettingsCopyForm.js's onGroupChange already has for the same callback shape.
        const groupId = option ? option.value : "";
        if(groupId !== ""){
            this.props.requestGetSolicitorGroupWorkSources(groupId);
            this.props.getSolicitorGroupCaseTypes(groupId);
        }

        this.setState({
            ...this.state,
            solicitorGroup: groupId,
            caseType: "",
            subCaseType: ""
        })
    };

    onWorkSourceChange = (evt) => {
        this.setState({
            ...this.state,
            workSource: evt.target.value
        })
    };

    onCaseChange = (evt) => {
        this.setState({
            ...this.state,
            caseType: evt.target.value,
            subCaseType: ""
        })
    };

    onSubCaseChange = (evt) => {
        this.setState({
            ...this.state,
            subCaseType: evt.target.value
        })
    };

    onCopy = () => {
        // ?copyFrom=<id> carries the source setting id to the create wizard - staged uses the staged
        // setting's id, every other type uses the matched flat setting's id (premiumSettingsId).
        // stagedId is '' for a non-staged copy (see PremiumSettings.js) and a real id otherwise, so
        // one !== '' check drives both the id and the route below.
        const isStagedCopy = this.props.stagedId !== '';
        const sourceSettingId = isStagedCopy ? this.props.stagedId : this.props.premiumSettingsId;
        const copyConfigQueryParam = `${PREMIUM_SETTINGS_COPY_FROM_PARAM}=${sourceSettingId}`;

        const matchedGroup = this.props.groupOptions.find(option => option.key === this.state.solicitorGroup);
        const matchedWorkSource = this.props.worksourcesOptions.find(option => option.key === this.state.workSource);
        // const matchedCaseType = this.props.caseTypes
        //     .find(type => type.id.toString() === this.state.caseType);
        // const matchedSubCaseType = this.props.subCaseTypes
        //     .find(type => type.id.toString() === this.state.caseType);

        this.props.changeNewPremiumStoreSettings("selectedSolicitorGroup", this.state.solicitorGroup);
        this.props.changeNewPremiumStoreSettings("selectedWorksource", this.state.workSource);
        this.props.changeNewPremiumStoreSettings("selectedCaseType", this.state.caseType );
        this.props.changeNewPremiumStoreSettings("selectedSubCaseType", this.state.subCaseType);

        // Bug fix: dispatched unconditionally (not just when matchedGroup exists) so a copy made
        // with no group selected (allowed for custom type) clears these instead of leaving the
        // previous copy's name and ARAG Provider colour on screen - the reducer only resets them on
        // CHANGE_PS_TYPE/full state reset, neither of which this copy-then-navigate flow triggers.
        this.props.changeNewPremiumStoreSettings("selectedSolicitorGroupName", matchedGroup?.label ?? '');
        // matchedGroup comes from groupOptions (PremiumSettings.js), which
        // already carries aragProvider alongside the name - set in parallel so the wizards'
        // read-only selected-group label can keep showing the ARAG Provider colour indicator.
        this.props.changeNewPremiumStoreSettings("selectedSolicitorGroupAragProvider", matchedGroup?.aragProvider ?? null);

        if(matchedWorkSource){
            this.props.changeNewPremiumStoreSettings("selectedWorkSourceName", matchedWorkSource.label);
        }

        isStagedCopy
            ? this.props.history.push(`premium-settings/new-staged-premium-settings?${copyConfigQueryParam}`)
            : this.props.history.push(`premium-settings/new-premium-settings?${copyConfigQueryParam}`)
    };

    render(){
        const {
            onCancel,
            groupCaseAndSubTypes,
            caseTypes,
            subCaseTypes,
            groupOptions,
            worksourcesOptions,
            premiumSettingType
        } = this.props;

        const caseTypesOptions = this.state.solicitorGroup 
            ? getCaseTypesOptions(groupCaseAndSubTypes, premiumSettingType) 
            : getCaseTypesOptions(caseTypes, premiumSettingType);

        const subCaseTypesOptions = this.state.solicitorGroup
            ? getGroupSubCaseTypesOptions(groupCaseAndSubTypes, this.state.caseType)
            : getSubCaseTypesOptions(subCaseTypes, this.state.caseType);

        // Only a Default Premium Setting can stay groupless - Custom, Percentage and Staged all
        // require a real solicitor group to be selected before the copy can proceed.
        const solGroupRejected = this.state.solicitorGroup === "" &&
            premiumSettingType?.toLowerCase() !== PS_TYPE_DEFAULT;

        // Bug fix: groupOptions carries INITIAL_OPTIONS' {key: '', label: ''} sentinel for the
        // native <select> variant it's shared with (PremiumSettings.js) - relabelled here to
        // CONTENT_STRINGS.NONE so the SearchableList below doesn't render it as a blank,
        // clickable row, matching the other converted SearchableList pickers.
        const solicitorGroupOptions = groupOptions.map(option => {
            return {
                value: option.key.toString(),
                label: option.key === '' ? CONTENT_STRINGS.NONE : option.label,
                aragProvider: option.aragProvider
            }
        });
        const solicitorGroupValue = solicitorGroupOptions.find(option => option.value.toString() === this.state.solicitorGroup) || null;
        
        return (
            <form className={"form-horizontal"}>
                <FormGroup
                    labelSize={'col-sm-3'}
                    inputSize={'col-sm-7'}
                    label={`${LEGAL_ADVICE_TEXT} ${CONTENT_STRINGS.GROUP}`}
                    variant={FORM_GROUP_VARIANTS.SEARCHABLE_LIST}
                    inputProps={{
                        instanceId: `${LEGAL_ADVICE_TEXT}Group`,
                        onChange: (option) => this.onGroupChange(option),
                        value: solicitorGroupValue,
                        options: solicitorGroupOptions,
                        formatOptionLabel: formatSolicitorOptionLabel,
                        required: premiumSettingType?.toLowerCase() !== PS_TYPE_DEFAULT
                    }}
                    formGroupTestId={`premium-settings-new-from-current-solicitor-group`}
                />
                {this.state.solicitorGroup !== "" 
                ?
                    <FormGroup 
                        labelSize={'col-sm-3'}
                        inputSize={'col-sm-7'}
                        label={CONTENT_STRINGS.WORK_SOURCE}
                        variant={FORM_GROUP_VARIANTS.SELECT}
                        inputProps={{
                            onChange: this.onWorkSourceChange,
                            value: this.state.workSource,
                            name: 'workSource',
                            options: worksourcesOptions,
                        }}
                        formGroupTestId={`premium-settings-new-from-current-work-source`}
                    />
                :
                    null
                }
                {!solGroupRejected
                ?
                    <FormGroup
                        labelSize={'col-sm-3'}
                        inputSize={'col-sm-7'}
                        label={CONTENT_STRINGS.CASE_TYPE}
                        variant={FORM_GROUP_VARIANTS.SELECT}
                        inputProps={{
                            id: 'premiumSettingsCopyFormSelectedCaseType',
                            onChange: this.onCaseChange,
                            value: this.state.caseType,
                            name: 'caseType',
                            options: caseTypesOptions,
                            required: true
                        }}
                        formGroupTestId={`premium-settings-new-from-current-case-type`}
                    /> 
                : 
                    null
                }
                {subCaseTypesOptions.length > 1 
                ?
                    <FormGroup
                        labelSize={'col-sm-3'}
                        inputSize={'col-sm-7'}
                        label={CONTENT_STRINGS.SUB_CASE_TYPE}
                        variant={FORM_GROUP_VARIANTS.SELECT}
                        inputProps={{
                            onChange: this.onSubCaseChange,
                            value: this.state.subCaseType,
                            name: 'subCaseType',
                            options: subCaseTypesOptions,
                        }}
                        formGroupTestId={`premium-settings-new-from-current-sub-case-type`}
                    />
                :
                    null
                }
                <div className={"form-group"}>
                    <div className={"col-sm-6 col-sm-offset-3"}>
                        <div style={{marginBottom: "20px"}}>
                            <Button
                                disabled={this.state.caseType === "" || solGroupRejected}
                                onClick={this.onCopy}
                                variant={PRIMARY}
                                label={CONTENT_STRINGS.CREATE_NEW_BASED_ON_CURRENT}
                                arrowDirection={RIGHT}
                                buttonTestId={`premium-settings-new-from-current-create-new-based-on-current`}
                            />
                        </div>
                        <Button
                            onClick={onCancel}
                            variant={TERTIARY}
                            label={CONTENT_STRINGS.CANCEL}
                            arrowDirection={RIGHT}
                            buttonTestId={`premium-settings-new-from-current-cancel`}
                        />
                    </div>
                </div>
            </form>
        )
    }
}

export default withRouter(PremiumSettingsCopyForm);